import React, { useState } from 'react'
import { Head } from '@inertiajs/react'
import { Stethoscope, Check, AlertTriangle, Loader2, MessageCircle } from 'lucide-react'

// PUBLIC finance application status at /finance/status/:token (Paul 2026-09-30). First name only. The
// timeline is built server-side (FinanceApplicationsController#timeline): one current step per status.
// The patient never sees a decline reason here — the lender gives reasons directly, as the NCA requires.

const card = 'rounded-2xl border border-brand-border bg-white p-5 shadow-sm'

export default function PublicFinanceStatus({ token, practice = {}, first_name, reference, lender, status, steps = [], info_request, can_upload, booking_link, limits = {} }) {
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(null)
  const [error, setError] = useState(null)

  async function upload(fileList) {
    const files = Array.from(fileList || [])
    if (!files.length) return
    if (files.length > 5) { setError('Please add up to 5 files at a time.'); return }
    const big = files.find((f) => f.size > (limits.max_file_mb || 10) * 1048576)
    if (big) { setError(`${big.name} is larger than ${limits.max_file_mb || 10} MB.`); return }
    setBusy(true); setError(null)
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content || ''
    const form = new FormData(); files.forEach((f) => form.append('files[]', f))
    try {
      const res = await fetch(`/finance/status/${token}/documents`, { method: 'POST', body: form, credentials: 'same-origin',
        headers: { Accept: 'application/json', 'X-CSRF-Token': csrf, 'X-Requested-With': 'XMLHttpRequest' } })
      const data = await res.json().catch(() => ({}))
      if (res.ok && data.ok) { setSent(files.length === 1 ? files[0].name : `${files.length} files`); setTimeout(() => window.location.reload(), 1500) }
      else setError(data.error || 'We couldn’t add that file. Please try again.')
    } catch (e) {
      setError('We couldn’t reach the practice. Check your connection and try again.')
    }
    setBusy(false)
  }

  return (
    <div className="min-h-screen bg-brand-surface/40 px-4 py-6">
      <Head title={`Your finance application — ${practice.name || 'Dr Chalita le Roux'}`} />
      <div className="mx-auto flex max-w-xl flex-col gap-5">
        <div className={card}>
          <div className="flex items-center gap-2 text-brand-primary"><Stethoscope size={18} /><span className="text-sm font-semibold">{practice.name || 'Dr Chalita le Roux'}</span></div>
          <p className="mt-2 text-xs font-semibold uppercase tracking-wider text-brand-primary">Your finance application</p>
          <h1 className="mt-1 text-xl font-semibold text-brand-ink">Hi {first_name || 'there'}</h1>
          <p className="mt-1 text-sm text-brand-muted">Application {reference} with {lender}. This page updates as it moves along, so you can come back to this link at any time.</p>
        </div>

        <div className={card}>
          <ol className="flex flex-col">
            {steps.map((s, i) => {
              const last = i === steps.length - 1
              const dot = s.state === 'done' ? 'border-emerald-600 bg-emerald-600 text-white'
                : s.state === 'stop' ? 'border-red-600 bg-red-600 text-white'
                : s.state === 'now' ? 'border-brand-primary text-brand-primary ring-4 ring-brand-accent'
                : 'border-brand-border text-brand-muted'
              return (
                <li key={s.key} className="relative grid grid-cols-[28px_1fr] gap-x-3 pb-5 last:pb-0">
                  {!last && <span className="absolute left-[13px] top-8 bottom-1 w-0.5 bg-brand-border" aria-hidden="true" />}
                  <span className={`flex h-7 w-7 items-center justify-center rounded-full border-2 bg-white text-xs font-bold ${dot}`}>
                    {s.state === 'done' ? <Check size={14} /> : s.state === 'stop' ? <AlertTriangle size={13} /> : i + 1}
                  </span>
                  <div>
                    <div className={`pt-0.5 font-semibold ${s.state === 'later' ? 'font-medium text-brand-muted' : 'text-brand-ink'}`}>{s.title}</div>
                    {s.text && <p className="mt-0.5 text-sm text-brand-muted">{s.text}</p>}
                    {s.key === 'info' && s.state === 'now' && can_upload && (
                      <div className="mt-3 flex flex-col gap-2">
                        {sent ? <p className="text-sm font-medium text-emerald-700">Added {sent}. Thank you.</p> : (
                          <label className="relative inline-flex cursor-pointer items-center justify-center gap-2 self-start rounded-lg border-2 border-brand-primary px-4 py-2 text-sm font-semibold text-brand-primary">
                            {busy ? <><Loader2 size={15} className="animate-spin" /> Sending…</> : 'Add the documents'}
                            <input type="file" multiple accept="application/pdf,image/*" className="absolute inset-0 cursor-pointer opacity-0" onChange={(e) => upload(e.target.files)} />
                          </label>
                        )}
                        {!sent && <p className="text-xs text-brand-muted">You can choose several files at once (up to 5). They go to {lender} together.</p>}
                        {error && <p className="text-sm text-red-600">{error}</p>}
                      </div>
                    )}
                    {s.key === 'ready' && s.state === 'now' && booking_link && (
                      <a href={booking_link} target="_blank" rel="noopener noreferrer"
                         className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-brand-primary px-4 py-3 text-sm font-semibold text-white transition hover:opacity-90">
                        <MessageCircle size={16} /> Book my first visit
                      </a>
                    )}
                  </div>
                </li>
              )
            })}
          </ol>
        </div>

        <p className="text-center text-xs text-brand-muted">
          Questions about your loan go to {lender}. Questions about your treatment: message us on WhatsApp
          {practice.wa ? <> at <a className="font-semibold text-brand-primary" href={`https://wa.me/${String(practice.wa).replace(/[^\d]/g, '')}`} target="_blank" rel="noreferrer">+{String(practice.wa).replace(/[^\d]/g, '')}</a></> : null}.
        </p>
      </div>
    </div>
  )
}
